import React, { useState, useEffect } from 'react';
import { FileText, Image as ImageIcon, Plus, Edit, Trash2, Save, X, Upload, ChevronLeft, ChevronRight, Layers, GripVertical, Eye, EyeOff, Search } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import { useBlocker } from 'react-router-dom';
import { getImageUrl } from '../../utils/helpers';
import { toast } from 'react-toastify';

const AdminContent = () => {
    const [activeTab, setActiveTab] = useState('news'); 
    const [activeAdTab, setActiveAdTab] = useState('MAIN_SLIDER');
    
    const [newsList, setNewsList] = useState([]);
    const [adsList, setAdsList] = useState([]);
    const [newsCatList, setNewsCatList] = useState([]);

    const [adForm, setAdForm] = useState({ 
        id: null, title: '', linkUrl: '', advertisePosition: 'MAIN_SLIDER',
        imageUrl: '', imageFile: null,                 // Ảnh mặc định
        imageMobileUrl: '', imageMobileFile: null,     // Ảnh Mobile
        imageTabletUrl: '', imageTabletFile: null,     // Ảnh Tablet
        imageDesktopUrl: '', imageDesktopFile: null    // Ảnh Desktop
    });

    const [newsCatForm, setNewsCatForm] = useState({ id: null, categoryName: '', description: '' });

    const [isAdFormDirty, setIsAdFormDirty] = useState(false);
    const [isNewsCatFormDirty, setIsNewsCatFormDirty] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6; 

    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('ALL');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    // Reset trang về 1 mỗi khi thay đổi điều kiện lọc
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterCategory, filterStartDate, filterEndDate]);

    // Drag & drop state cho banner
    const [draggedBanner, setDraggedBanner] = useState(null);
    const [dragOverBannerId, setDragOverBannerId] = useState(null);

    const [mainSliderPage, setMainSliderPage] = useState(1);
    const [subBannerPage, setSubBannerPage] = useState(1);
    const bannersPerPage = 10; // Số banner mỗi trang

    const hasUnsavedChanges = isAdFormDirty || isNewsCatFormDirty;
    const blocker = useBlocker(() => hasUnsavedChanges);
    

    const fetchData = async () => {
        try {
            const [newsData, adsData, newsCatData] = await Promise.all([
                adminStaffService.getAllNews().catch(()=>[]),
                adminStaffService.getAllAdvertises().catch(()=>[]),
                adminStaffService.getAllNewsCategories().catch(()=>[]) 
            ]);

            const rawNews = newsData?.data || newsData || [];
            const sortedNews = [...rawNews].sort((a, b) => {
                const dateA = new Date(a.newsCreatedTime || a.createdAt || 0).getTime();
                const dateB = new Date(b.newsCreatedTime || b.createdAt || 0).getTime();
                return dateB - dateA;
            });

            setNewsList(sortedNews);

            // Sort banner theo advertiseOrder
            const rawAds = adsData?.data || adsData || [];
            const sortedAds = [...rawAds].sort((a, b) => (a.advertiseOrder || 999) - (b.advertiseOrder || 999));
            setAdsList(sortedAds);

            setNewsCatList(newsCatData?.data || newsCatData || []);
        } catch (error) { console.error(error); }
    };

    useEffect(() => { fetchData(); }, []);

    // ==========================================
    // XỬ LÝ TIN TỨC (NEWS)
    // ==========================================
    const handleDeleteNews = async (id) => {
        if(!window.confirm("Chắc chắn xóa bài viết tin tức này?")) return;
        try { 
            await adminStaffService.deleteNews(id); 
            toast.success("Xóa bài viết thành công! 🎉");
            fetchData(); 
        } catch (e) { toast.error("Lỗi xóa bài viết!"); }
    };

    // ==========================================
    // XỬ LÝ DANH MỤC TIN TỨC (NEWS CATEGORIES)
    // ==========================================
    const handleSaveNewsCat = async (e) => {
        e.preventDefault();
        try {
            if (newsCatForm.id) {
                await adminStaffService.updateNewsCategory(newsCatForm.id, newsCatForm);
            } else {
                await adminStaffService.createNewsCategory(newsCatForm);
            }
            setNewsCatForm({ id: null, categoryName: '', description: '' });
            setIsNewsCatFormDirty(false); // 👈 Reset dirty sau khi save
            toast.success(newsCatForm.id ? "Cập nhật danh mục thành công!" : "Thêm danh mục thành công!");
            fetchData();
        } catch (e) { toast.error("Lỗi khi lưu danh mục!"); }
    };

    const handleDeleteNewsCat = async (id) => {
        if(!window.confirm("Xóa danh mục này? Các bài viết thuộc danh mục có thể bị ảnh hưởng!")) return;
        try {
            await adminStaffService.deleteNewsCategory(id);
            toast.success("Xóa danh mục thành công!");
            fetchData();
        } catch (e) { toast.error("Lỗi xóa danh mục!"); }
    };

    // ==========================================
    // XỬ LÝ BANNERS / QUẢNG CÁO
    // ==========================================

    const handleEditAd = (ad) => {
        if (isAdFormDirty) {
            if (!window.confirm('Bạn đang có thay đổi chưa lưu ở Form bên trái. Nếu chuyển sang sửa banner này, các dữ liệu vừa nhập sẽ bị mất. Bạn có chắc chắn muốn tiếp tục?')) {
                return; // Nếu bấm Hủy thì không làm gì cả
            }
        }
        
        // Nếu đồng ý (hoặc form chưa có thay đổi gì), thì load data mới vào form
        setAdForm({ 
            id: ad.advertiseId, 
            title: ad.advertiseTitle, 
            linkUrl: ad.advertiseUrl || '', 
            advertisePosition: ad.advertisePosition || 'MAIN_SLIDER', 
            imageUrl: ad.advertiseImageUrl, imageFile: null,
            imageMobileUrl: ad.imageMobile, imageMobileFile: null,
            imageTabletUrl: ad.imageTablet, imageTabletFile: null,
            imageDesktopUrl: ad.imageDesktop, imageDesktopFile: null
        });
        
        // Reset lại trạng thái dirty
        setIsAdFormDirty(false); 
    };
    const handleResponsiveImageSelect = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            setAdForm(prev => ({ 
                ...prev, 
                [`${type}File`]: file, 
                [`${type}Url`]: URL.createObjectURL(file) 
            }));
            setIsAdFormDirty(true); // 👈 Mark dirty khi chọn ảnh
        }
    };

    const handleSaveAd = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append("data", JSON.stringify({ 
                advertiseTitle: adForm.title, 
                advertiseUrl: adForm.linkUrl,
                advertisePosition: adForm.advertisePosition 
            }));

            if (adForm.imageFile) formData.append("image", adForm.imageFile);
            if (adForm.imageMobileFile) formData.append("imageMobile", adForm.imageMobileFile);
            if (adForm.imageTabletFile) formData.append("imageTablet", adForm.imageTabletFile);
            if (adForm.imageDesktopFile) formData.append("imageDesktop", adForm.imageDesktopFile);

            if (adForm.id) { await adminStaffService.updateAdvertise(adForm.id, formData); } 
            else { await adminStaffService.createAdvertise(formData); }
            
            setAdForm({ 
                id: null, title: '', imageUrl: '', linkUrl: '', imageFile: null, 
                advertisePosition: 'MAIN_SLIDER',
                imageMobileUrl: '', imageMobileFile: null,
                imageTabletUrl: '', imageTabletFile: null,
                imageDesktopUrl: '', imageDesktopFile: null
            });
            setIsAdFormDirty(false); // 👈 Reset dirty sau khi save
            toast.success(adForm.id ? "Cập nhật Banner thành công!" : "Thêm Banner thành công!");
            fetchData();
        } catch (e) { toast.error("Lỗi khi lưu Banner!"); }
    };

    // Hard delete — xóa thật sự khỏi DB
    const handleDeleteAd = async (id) => {
        if(!window.confirm("Xóa Banner này? Hành động không thể hoàn tác!")) return;
        try { 
            await adminStaffService.deleteAdvertise(id); 
            toast.success("Xóa Banner thành công!");
            fetchData(); 
        } catch (e) { toast.error("Lỗi xóa Banner!"); }
    };

    const filteredNewsList = newsList.filter(item => {
        // 1. Lọc theo từ khóa (Tìm trong tiêu đề)
        const term = searchTerm.toLowerCase().trim();
        const matchesSearch = term === '' || (item.newsTitle && item.newsTitle.toLowerCase().includes(term));

        // 2. Lọc theo danh mục
        const selectedCat = newsCatList.find(c => String(c.categoryId) === filterCategory);
        const matchesCategory = filterCategory === 'ALL' || 
            String(item.categoryId) === filterCategory || 
            (selectedCat && item.categoryName === selectedCat.categoryName);

        // 3. Lọc theo khoảng thời gian
        const itemDate = new Date(item.newsCreatedTime || item.createdAt || 0).getTime();
        const start = filterStartDate ? new Date(filterStartDate).setHours(0, 0, 0, 0) : 0;
        const end = filterEndDate ? new Date(filterEndDate).setHours(23, 59, 59, 999) : Infinity;
        const matchesDate = itemDate >= start && itemDate <= end;

        return matchesSearch && matchesCategory && matchesDate;
    });

    // 👉 CẬP NHẬT LẠI BIẾN PHÂN TRANG (Dùng filteredNewsList thay vì newsList)
    const indexOfLastNews = currentPage * itemsPerPage;
    const indexOfFirstNews = indexOfLastNews - itemsPerPage;
    const currentNewsList = filteredNewsList.slice(indexOfFirstNews, indexOfLastNews);
    const totalPages = Math.max(1, Math.ceil(filteredNewsList.length / itemsPerPage)); 
    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    // ==========================================
    // DRAG & DROP REORDER BANNER (chỉ MAIN_SLIDER)
    // ==========================================
    const mainSliders = adsList.filter(a => a.advertisePosition === 'MAIN_SLIDER' || !a.advertisePosition);
    const subBanners  = adsList.filter(a => a.advertisePosition === 'SUB_BANNER');

    const indexOfLastMainSlider = mainSliderPage * bannersPerPage;
    const indexOfFirstMainSlider = indexOfLastMainSlider - bannersPerPage;
    const currentMainSliders = mainSliders.slice(indexOfFirstMainSlider, indexOfLastMainSlider);
    const totalMainSliderPages = Math.ceil(mainSliders.length / bannersPerPage);

    const indexOfLastSubBanner = subBannerPage * bannersPerPage;
    const indexOfFirstSubBanner = indexOfLastSubBanner - bannersPerPage;
    const currentSubBanners = subBanners.slice(indexOfFirstSubBanner, indexOfLastSubBanner);
    const totalSubBannerPages = Math.ceil(subBanners.length / bannersPerPage);

    const handleBannerDragStart = (e, banner) => {
        setDraggedBanner(banner);
        e.dataTransfer.effectAllowed = "move";
        e.target.style.opacity = "0.4";
    };

    const handleBannerDragEnd = (e) => {
        e.target.style.opacity = "1";
        setDraggedBanner(null);
        setDragOverBannerId(null);
    };

    const handleBannerDrop = async (e, targetBanner, type = 'MAIN_SLIDER') => {
        e.preventDefault();
        setDragOverBannerId(null);
        if (!draggedBanner || draggedBanner.advertiseId === targetBanner.advertiseId) return;

        const isMain = type === 'MAIN_SLIDER';
        const pageItems = isMain ? [...currentMainSliders] : [...currentSubBanners];
        const allItems = isMain ? [...mainSliders] : [...subBanners];
        const indexOffset = isMain ? indexOfFirstMainSlider : indexOfFirstSubBanner;

        const from = pageItems.findIndex(a => a.advertiseId === draggedBanner.advertiseId);
        const to   = pageItems.findIndex(a => a.advertiseId === targetBanner.advertiseId);
        
        if (from === -1 || to === -1) {
            toast.warn("Chỉ có thể kéo thả trong cùng 1 trang!");
            return;
        }

        const realFrom = indexOffset + from;
        const realTo   = indexOffset + to;

        const [moved] = allItems.splice(realFrom, 1);
        allItems.splice(realTo, 0, moved);

        // Cập nhật lại list tổng
        if (isMain) setAdsList([...allItems, ...subBanners]);
        else setAdsList([...mainSliders, ...allItems]);

        const ids = allItems.map(a => a.advertiseId);
        try {
            await adminStaffService.reorderAdvertises(ids);
            toast.success("Đã lưu thứ tự banner!");
        } catch {
            toast.error("Lỗi khi lưu thứ tự!");
            fetchData();
        }
    };

    const handleManualReorderBanner = async (banner, newPos, type = 'MAIN_SLIDER') => {
        if (!newPos || isNaN(newPos)) return;
        const parsed = parseInt(newPos);
        const isMain = type === 'MAIN_SLIDER';
        const allItems = isMain ? [...mainSliders] : [...subBanners];
        
        if (parsed <= 0 || parsed > allItems.length) {
            toast.warn(`Vị trí phải từ 1 đến ${allItems.length}`);
            return;
        }

        const from = allItems.findIndex(a => a.advertiseId === banner.advertiseId);
        const to   = parsed - 1; 
        
        if (from === to || from === -1) return;

        const [moved] = allItems.splice(from, 1);
        allItems.splice(to, 0, moved);

        if (isMain) setAdsList([...allItems, ...subBanners]);
        else setAdsList([...mainSliders, ...allItems]);

        const ids = allItems.map(a => a.advertiseId);
        try {
            await adminStaffService.reorderAdvertises(ids);
            toast.success("Đã lưu thứ tự banner!");
            
            // Nhảy đến trang chứa vị trí mới
            const newPage = Math.ceil(parsed / bannersPerPage);
            if (isMain) setMainSliderPage(newPage);
            else setSubBannerPage(newPage);
        } catch {
            toast.error("Lỗi khi lưu thứ tự!");
            fetchData();
        }
    };

    const handleToggleAdStatus = async (id, currentStatus) => {
        try {
            await adminStaffService.toggleAdvertiseStatus(id);
            toast.success(currentStatus ? "Đã tắt banner!" : "Đã bật banner!");
            fetchData();
        } catch { toast.error("Lỗi khi thay đổi trạng thái!"); }
    };

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleTabChange = (newTab) => {
        if (hasUnsavedChanges) {
            if (!window.confirm('Bạn có thay đổi chưa lưu. Chuyển tab sẽ mất dữ liệu. Tiếp tục?')) {
                return;
            }
            // Reset dirty state nếu user confirm
            setIsAdFormDirty(false);
            setIsNewsCatFormDirty(false);
        }
        setActiveTab(newTab);
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Quản lý Nội dung (CMS)</h2>
            
            {/* ĐIỀU HƯỚNG TAB */}
            <div className="flex gap-4 border-b border-gray-200 mb-6">
                <button 
                    onClick={() => handleTabChange('news')} 
                    className={`pb-3 font-bold px-4 border-b-2 transition-colors ${activeTab === 'news' ? 'border-tnc-orange text-tnc-orange' : 'border-transparent text-gray-500 hover:text-tnc-orange'}`}
                >
                    Tin tức (News)
                </button>
                <button 
                    onClick={() => handleTabChange('news_cat')} 
                    className={`pb-3 font-bold px-4 border-b-2 transition-colors relative ${activeTab === 'news_cat' ? 'border-tnc-orange text-tnc-orange' : 'border-transparent text-gray-500 hover:text-tnc-orange'}`}
                >
                    Danh mục Tin tức
                    {/* 👉 BADGE "Đã sửa" */}
                    {isNewsCatFormDirty && (
                        <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                            Đã sửa
                        </span>
                    )}
                </button>
                <button 
                    onClick={() => handleTabChange('ads')} 
                    className={`pb-3 font-bold px-4 border-b-2 transition-colors relative ${activeTab === 'ads' ? 'border-tnc-orange text-tnc-orange' : 'border-transparent text-gray-500 hover:text-tnc-orange'}`}
                >
                    Banners / Quảng cáo
                    {/* 👉 BADGE "Đã sửa" */}
                    {isAdFormDirty && (
                        <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                            Đã sửa
                        </span>
                    )}
                </button>
            </div>

            {/* ================= TAB TIN TỨC ================= */}
            {activeTab === 'news' && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6 border-b border-gray-100 pb-4">
                        <h3 className="font-bold text-xl text-gray-800 flex items-center gap-2">
                            <FileText size={24} className="text-tnc-orange"/> Danh sách bài viết ({filteredNewsList.length})
                        </h3>
                        <a
                        href="/admin/news/create"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-tnc-orange hover:bg-tnc-orange-hover text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 transition-colors shadow-sm"
                        >
                            <Plus size={18} /> Viết bài mới
                        </a>
                    </div>

                    {/* 👉 THANH CÔNG CỤ TÌM KIẾM VÀ LỌC */}
                    <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-6 bg-gray-50/50 p-4 rounded-xl border border-gray-200">
                        {/* Ô Tìm kiếm */}
                        <div className="flex-1 relative">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input 
                                type="text" 
                                placeholder="Tìm kiếm theo tiêu đề..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-[40px] pl-10 pr-4 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange text-sm transition-colors bg-white"
                            />
                        </div>
                        
                        {/* Lọc danh mục */}
                        <div className="w-full lg:w-48">
                            <select 
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                className="w-full h-[40px] px-3 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange text-sm font-medium bg-white text-gray-700 cursor-pointer"
                            >
                                <option value="ALL">Tất cả danh mục</option>
                                {newsCatList.map(cat => (
                                    <option key={cat.categoryId} value={cat.categoryId}>
                                        {cat.categoryName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Lọc ngày đăng */}
                        <div className="flex items-center gap-2 w-full lg:w-auto">
                            <div className="flex flex-col w-1/2 lg:w-36">
                                <span className="text-[10px] font-bold text-gray-500 mb-1 ml-1">Từ ngày</span>
                                <input 
                                    type="date" 
                                    value={filterStartDate}
                                    onChange={(e) => setFilterStartDate(e.target.value)}
                                    className="w-full h-[40px] px-3 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange text-sm bg-white"
                                />
                            </div>
                            <div className="flex flex-col w-1/2 lg:w-36">
                                <span className="text-[10px] font-bold text-gray-500 mb-1 ml-1">Đến ngày</span>
                                <input 
                                    type="date" 
                                    value={filterEndDate}
                                    onChange={(e) => setFilterEndDate(e.target.value)}
                                    className="w-full h-[40px] px-3 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange text-sm bg-white"
                                />
                            </div>
                        </div>
                        
                        {/* Nút Xóa bộ lọc (Chỉ hiện khi có áp dụng lọc) */}
                        {(searchTerm || filterCategory !== 'ALL' || filterStartDate || filterEndDate) && (
                            <div className="flex items-end">
                                <button 
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilterCategory('ALL');
                                        setFilterStartDate('');
                                        setFilterEndDate('');
                                    }}
                                    className="px-4 h-[40px] text-sm font-bold text-red-500 bg-red-50 hover:bg-red-500 hover:text-white rounded-lg transition-colors border border-red-100 flex items-center justify-center w-full lg:w-auto"
                                >
                                    <X size={16} className="mr-1"/> Xóa lọc
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-3 min-h-[400px]">
                        {currentNewsList.map(item => (
                            <div key={item.newsId} className="flex items-center p-3 border border-gray-200 rounded-xl hover:border-tnc-orange hover:bg-orange-50/30 transition-all bg-white group relative">
                                {item.categoryName && (
                                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm z-10">
                                        {item.categoryName}
                                    </div>
                                )}
                                <div className="w-24 h-16 md:w-32 md:h-20 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden border border-gray-100 relative">
                                    <img src={getImageUrl(item.newsImageUrl || item.newsImage)} alt={item.newsTitle} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 flex flex-col justify-center px-4 overflow-hidden">
                                    <h4 className="font-bold text-gray-800 text-sm md:text-base line-clamp-1 group-hover:text-tnc-orange transition-colors" title={item.newsTitle}>
                                        {item.newsTitle}
                                    </h4>
                                    <div className="flex items-center gap-3 mt-1">
                                        <span className="text-[11px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded uppercase tracking-wider">
                                            {new Date(item.newsCreatedTime || item.createdAt).toLocaleDateString('vi-VN')}
                                        </span>
                                        <p className="text-xs text-gray-500 line-clamp-1 truncate">
                                            {item.newsExcerpt || item.newsContent?.replace(/<[^>]*>?/gm, '')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 pl-2 border-l border-gray-100">
                                    <a
                                    href={`/admin/news/edit/${item.newsId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-500 hover:text-white transition-colors"
                                        title="Sửa bài viết"
                                    >
                                        <Edit size={16}/>
                                    </a>
                                    <button onClick={() => handleDeleteNews(item.newsId)} className="p-2 bg-red-50 text-red-500 rounded hover:bg-red-500 hover:text-white transition-colors" title="Xóa bài viết"><Trash2 size={16}/></button>
                                </div>
                            </div>
                        ))}
                        {newsList.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                                <FileText size={48} className="mb-3 opacity-20"/>
                                <p className="font-medium">Chưa có bài viết nào. Hãy bấm "Viết bài mới" để bắt đầu!</p>
                            </div>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="flex flex-col md:flex-row items-center justify-between mt-6 pt-4 border-t border-gray-100 gap-4">
                            <span className="text-sm text-gray-500 font-medium">
                                Hiển thị <strong className="text-gray-900">{indexOfFirstNews + 1} - {Math.min(indexOfLastNews, newsList.length)}</strong> trên tổng số <strong className="text-gray-900">{newsList.length}</strong> bài viết
                            </span>
                            
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex gap-1">
                                    <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-2 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"><ChevronLeft size={18} /></button>
                                    {[...Array(totalPages)].map((_, index) => (
                                        <button key={index} onClick={() => handlePageChange(index + 1)} className={`w-9 h-9 rounded font-bold text-sm transition-colors border ${currentPage === index + 1 ? 'bg-tnc-orange text-white border-tnc-orange' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{index + 1}</button>
                                    ))}
                                    <button onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-2 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"><ChevronRight size={18} /></button>
                                </div>

                                <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                                    <span className="text-sm text-gray-500 font-medium">Đến trang:</span>
                                    <input 
                                        key={`news-${currentPage}`}
                                        type="number" min={1} max={totalPages} defaultValue={currentPage}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const page = parseInt(e.target.value);
                                                if (page >= 1 && page <= totalPages) handlePageChange(page);
                                                else e.target.value = currentPage;
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const page = parseInt(e.target.value);
                                            if (page >= 1 && page <= totalPages && page !== currentPage) handlePageChange(page);
                                            else e.target.value = currentPage;
                                        }}
                                        className="w-16 h-9 px-2 text-center border border-gray-300 rounded outline-none text-sm font-bold focus:border-tnc-orange"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ================= TAB DANH MỤC TIN TỨC ================= */}
            {activeTab === 'news_cat' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-fit">
                        <h3 className="font-bold mb-4 flex items-center gap-2 text-gray-800">
                            <Layers size={20} className="text-tnc-orange"/> {newsCatForm.id ? 'Sửa Danh mục' : 'Thêm Danh mục Mới'}
                            {isNewsCatFormDirty && (
                                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full animate-pulse">
                                    Chưa lưu
                                </span>
                            )}
                        </h3>
                        <form onSubmit={handleSaveNewsCat} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Tên danh mục *</label>
                                <input 
                                    type="text" 
                                    value={newsCatForm.categoryName} 
                                    onChange={e => {
                                        setNewsCatForm({...newsCatForm, categoryName: e.target.value});
                                        setIsNewsCatFormDirty(true); // 👈 Mark dirty
                                    }}
                                    required 
                                    className="w-full border border-gray-200 rounded px-3 py-2 outline-none focus:border-tnc-orange"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Mô tả</label>
                                <textarea 
                                    value={newsCatForm.description} 
                                    onChange={e => {
                                        setNewsCatForm({...newsCatForm, description: e.target.value});
                                        setIsNewsCatFormDirty(true); // 👈 Mark dirty
                                    }}
                                    rows="3" 
                                    className="w-full border border-gray-200 rounded px-3 py-2 outline-none focus:border-tnc-orange resize-none"
                                />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="submit" className="flex-1 bg-tnc-orange text-white py-2 rounded font-bold flex justify-center items-center gap-2 hover:bg-tnc-orange-hover transition-colors shadow-sm">
                                    {newsCatForm.id ? <Save size={18}/> : <Plus size={18}/>} {newsCatForm.id ? 'Cập nhật' : 'Thêm mới'}
                                </button>
                                {newsCatForm.id && (
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            setNewsCatForm({id: null, categoryName: '', description: ''});
                                            setIsNewsCatFormDirty(false); // 👈 Reset dirty
                                        }}
                                        className="px-4 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                                    >
                                        <X size={18}/>
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="font-bold mb-4 text-gray-800">Danh sách các Chuyên mục ({newsCatList.length})</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto pr-2">
                            {newsCatList.map(cat => (
                                <div key={cat.categoryId} className={`p-4 border rounded-xl relative transition-all duration-300 bg-gray-50 border-gray-200 shadow-sm hover:border-tnc-orange hover:bg-white ${newsCatForm.id === cat.categoryId ? 'ring-2 ring-orange-400 scale-[1.02]' : ''}`}>
                                    <h5 className="font-extrabold text-[#002855] text-lg line-clamp-1">{cat.categoryName}</h5>
                                    <p className="font-medium text-gray-500 text-sm mt-1 line-clamp-2">{cat.description || 'Chưa có mô tả'}</p>
                                    <div className="absolute top-3 right-3 flex gap-1">
                                        <button onClick={() => setNewsCatForm({id: cat.categoryId, categoryName: cat.categoryName, description: cat.description || ''})} className="p-1.5 bg-blue-50 text-blue-500 rounded hover:bg-blue-600 hover:text-white transition-colors" title="Sửa"><Edit size={16}/></button>
                                        <button onClick={() => handleDeleteNewsCat(cat.categoryId)} className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-600 hover:text-white transition-colors" title="Xóa"><Trash2 size={16}/></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ================= TAB BANNERS ================= */}
            {activeTab === 'ads' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-fit">
                        <h3 className="font-bold mb-4 flex items-center gap-2 text-gray-800">
                            <ImageIcon size={20} className="text-tnc-orange"/> 
                            {adForm.id ? 'Sửa Banner' : 'Thêm Banner Mới'}
                            {/* 👉 BADGE "Chưa lưu" */}
                            {isAdFormDirty && (
                                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full animate-pulse">
                                    Chưa lưu
                                </span>
                            )}
                        </h3>
                        <form onSubmit={handleSaveAd} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Tên chiến dịch *</label>
                                <input 
                                    type="text" 
                                    value={adForm.title} 
                                    onChange={e => {
                                        setAdForm({...adForm, title: e.target.value});
                                        setIsAdFormDirty(true); // 👈 Mark dirty
                                    }}
                                    required 
                                    className="w-full border border-gray-200 rounded px-3 py-2 outline-none focus:border-tnc-orange"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Vị trí hiển thị *</label>
                                <select 
                                    value={adForm.advertisePosition} 
                                    onChange={e => {
                                        setAdForm({...adForm, advertisePosition: e.target.value});
                                        setIsAdFormDirty(true); // 👈 Mark dirty
                                    }}
                                    required 
                                    className="w-full border border-gray-200 rounded px-3 py-2 outline-none focus:border-tnc-orange bg-gray-50 font-medium"
                                >
                                    <option value="MAIN_SLIDER">Slider chính (Trượt phía trên)</option>
                                    <option value="SUB_BANNER">Banner phụ (2 ảnh nhỏ phía dưới)</option>
                                </select>
                            </div>
                            <div>
                                <div>
                                    <label className="block text-sm font-bold mb-2 text-gray-700">Hình ảnh Banner (Responsive) *</label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        
                                        {/* Box 1: Ảnh Default / Mặc định */}
                                        <div className="border border-gray-200 p-3 rounded-lg bg-gray-50">
                                            <span className="text-xs font-bold text-gray-600 mb-2 block">Ảnh Mặc định (Bắt buộc)</span>
                                            {adForm.imageUrl && (
                                                <div className="h-20 bg-gray-200 rounded mb-2 overflow-hidden">
                                                    <img src={getImageUrl(adForm.imageUrl)} alt="Preview" className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <label className="flex items-center justify-center gap-1 bg-white border border-gray-300 py-1.5 rounded cursor-pointer hover:bg-gray-100 text-xs font-medium">
                                                <Upload size={14} /> Tải ảnh lên
                                                <input type="file" accept="image/*" onChange={(e) => handleResponsiveImageSelect(e, 'image')} className="hidden" />
                                            </label>
                                        </div>

                                        {/* Box 2: Ảnh Desktop */}
                                        <div className="border border-gray-200 p-3 rounded-lg bg-gray-50">
                                            <span className="text-xs font-bold text-gray-600 mb-2 block">Desktop (Nên dùng: 1920 × 768px, tỉ lệ 2.5:1)</span>
                                            {adForm.imageDesktopUrl && (
                                                <div className="h-20 bg-gray-200 rounded mb-2 overflow-hidden">
                                                    <img src={getImageUrl(adForm.imageDesktopUrl)} alt="Preview" className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <label className="flex items-center justify-center gap-1 bg-white border border-gray-300 py-1.5 rounded cursor-pointer hover:bg-gray-100 text-xs font-medium">
                                                <Upload size={14} /> Tải ảnh lên
                                                <input type="file" accept="image/*" onChange={(e) => handleResponsiveImageSelect(e, 'imageDesktop')} className="hidden" />
                                            </label>
                                        </div>

                                        {/* Box 3: Ảnh Tablet */}
                                        <div className="border border-gray-200 p-3 rounded-lg bg-gray-50">
                                            <span className="text-xs font-bold text-gray-600 mb-2 block">Tablet (Nên dùng: 1280 × 720px, tỉ lệ 16:9)</span>
                                            {adForm.imageTabletUrl && (
                                                <div className="h-20 bg-gray-200 rounded mb-2 overflow-hidden">
                                                    <img src={getImageUrl(adForm.imageTabletUrl)} alt="Preview" className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <label className="flex items-center justify-center gap-1 bg-white border border-gray-300 py-1.5 rounded cursor-pointer hover:bg-gray-100 text-xs font-medium">
                                                <Upload size={14} /> Tải ảnh lên
                                                <input type="file" accept="image/*" onChange={(e) => handleResponsiveImageSelect(e, 'imageTablet')} className="hidden" />
                                            </label>
                                        </div>

                                        {/* Box 4: Ảnh Mobile */}
                                        <div className="border border-gray-200 p-3 rounded-lg bg-gray-50">
                                            <span className="text-xs font-bold text-gray-600 mb-2 block">Mobile (Nên dùng: 750 × 600px, tỉ lệ 5:4)</span>
                                            {adForm.imageMobileUrl && (
                                                <div className="h-20 bg-gray-200 rounded mb-2 overflow-hidden">
                                                    <img src={getImageUrl(adForm.imageMobileUrl)} alt="Preview" className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <label className="flex items-center justify-center gap-1 bg-white border border-gray-300 py-1.5 rounded cursor-pointer hover:bg-gray-100 text-xs font-medium">
                                                <Upload size={14} /> Tải ảnh lên
                                                <input type="file" accept="image/*" onChange={(e) => handleResponsiveImageSelect(e, 'imageMobile')} className="hidden" />
                                            </label>
                                        </div>

                                    </div>
                                    <p className="text-[10px] text-gray-500 mt-2 italic">* Nếu bỏ trống các ô Tablet/Mobile/Desktop, hệ thống sẽ tự động dùng Ảnh Mặc định.</p>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Link đích (Khi click)</label>
                                <input 
                                    type="text" 
                                    value={adForm.linkUrl} 
                                    onChange={e => {
                                        setAdForm({...adForm, linkUrl: e.target.value});
                                        setIsAdFormDirty(true); // 👈 Mark dirty
                                    }}
                                    placeholder="VD: /category/1" 
                                    className="w-full border border-gray-200 rounded px-3 py-2 outline-none focus:border-tnc-orange"
                                />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="submit" className="flex-1 bg-tnc-orange text-white py-2 rounded font-bold flex justify-center items-center gap-2 hover:bg-tnc-orange-hover transition-colors shadow-sm">
                                    {adForm.id ? <Save size={18}/> : <Plus size={18}/>} {adForm.id ? 'Cập nhật' : 'Thêm Banner'}
                                </button>
                                {adForm.id && (
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            setAdForm({
                                                id: null, title: '', imageUrl: '', linkUrl: '', imageFile: null, 
                                                advertisePosition: 'MAIN_SLIDER',
                                                imageMobileUrl: '', imageMobileFile: null,
                                                imageTabletUrl: '', imageTabletFile: null,
                                                imageDesktopUrl: '', imageDesktopFile: null
                                            });
                                            setIsAdFormDirty(false); // 👈 Reset dirty
                                        }}
                                        className="px-4 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                                    >
                                        <X size={18}/>
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* DANH SÁCH BANNER */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex items-center justify-between mb-5 border-b border-gray-200">
                            <h3 className="font-bold text-gray-800 pb-4">Danh sách Banners ({adsList.length})</h3>
                            
                            {/* SUB-TABS NAVIGATION */}
                            <div className="flex gap-4">
                                <button 
                                    onClick={() => setActiveAdTab('MAIN_SLIDER')}
                                    className={`pb-4 px-2 text-sm font-bold border-b-2 transition-colors -mb-[1px] ${activeAdTab === 'MAIN_SLIDER' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-blue-500'}`}
                                >
                                    Slider Chính ({mainSliders.length})
                                </button>
                                <button 
                                    onClick={() => setActiveAdTab('SUB_BANNER')}
                                    className={`pb-4 px-2 text-sm font-bold border-b-2 transition-colors -mb-[1px] ${activeAdTab === 'SUB_BANNER' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-400 hover:text-green-500'}`}
                                >
                                    Banner Phụ ({subBanners.length})
                                </button>
                            </div>
                        </div>

                        {/* ================= TAB: MAIN SLIDER ================= */}
                        {activeAdTab === 'MAIN_SLIDER' && (
                            <div className="animate-fadeIn">
                                <div className="flex justify-end mb-3">
                                    <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                        <GripVertical size={10}/> Kéo thả hoặc nhập số để sắp xếp
                                    </span>
                                </div>

                                {mainSliders.length === 0 ? (
                                    <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-sm text-gray-400">
                                        Chưa có Slider Chính nào
                                    </div>
                                ) : (
                                    <>
                                        <ul className="flex flex-col gap-2">
                                            {currentMainSliders.map((ad, idx) => {
                                                const globalIndex = indexOfFirstMainSlider + idx;
                                                return (
                                                    <li
                                                        key={ad.advertiseId} draggable
                                                        onDragStart={(e) => handleBannerDragStart(e, ad)}
                                                        onDragEnd={handleBannerDragEnd}
                                                        onDragOver={(e) => { e.preventDefault(); setDragOverBannerId(ad.advertiseId); }}
                                                        onDragLeave={() => setDragOverBannerId(null)}
                                                        onDrop={(e) => handleBannerDrop(e, ad, 'MAIN_SLIDER')}
                                                        className={`flex items-center gap-3 p-2 border rounded-xl transition-all cursor-grab active:cursor-grabbing
                                                            ${draggedBanner?.advertiseId === ad.advertiseId ? 'opacity-40 bg-blue-50 border-blue-500' 
                                                            : dragOverBannerId === ad.advertiseId ? 'border-blue-500 bg-blue-50 scale-[1.01]'
                                                            : 'bg-white border-gray-200 hover:border-blue-300 shadow-sm'}`}
                                                    >
                                                        <GripVertical size={16} className="text-gray-300 shrink-0 select-none" />
                                                        <input
                                                            type="number" min="1" max={mainSliders.length} defaultValue={globalIndex + 1}
                                                            key={`order-main-${ad.advertiseId}-${globalIndex}`}
                                                            onBlur={(e) => handleManualReorderBanner(ad, e.target.value, 'MAIN_SLIDER')}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            onClick={(e) => e.stopPropagation()} onDragStart={(e) => e.stopPropagation()}
                                                            className="w-12 h-7 text-center text-xs font-bold border border-gray-200 rounded outline-none focus:border-blue-500 cursor-text shrink-0"
                                                            title={`Vị trí: ${globalIndex + 1}/${mainSliders.length}`}
                                                        />
                                                        <div className="w-20 h-12 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shrink-0">
                                                            <img src={getImageUrl(ad.advertiseImageUrl)} alt={ad.advertiseTitle} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = getImageUrl(null); }} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-bold text-gray-800 truncate">{ad.advertiseTitle}</p>
                                                            <p className="text-[11px] text-blue-500 truncate">{ad.advertiseUrl || 'Không có link đích'}</p>
                                                        </div>
                                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${ad.advertiseStatus ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                                            {ad.advertiseStatus ? 'ON' : 'OFF'}
                                                        </span>
                                                        <div className="flex gap-1 shrink-0">
                                                            <button onClick={() => handleEditAd(ad)} className="p-1.5 bg-blue-50 text-blue-500 rounded hover:bg-blue-500 hover:text-white transition-colors" title="Sửa"><Edit size={14}/></button>
                                                            <button onClick={() => handleDeleteAd(ad.advertiseId)} className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-500 hover:text-white transition-colors" title="Xóa"><Trash2 size={14}/></button>
                                                            <button onClick={() => handleToggleAdStatus(ad.advertiseId, ad.advertiseStatus)} className={`p-1.5 rounded transition-colors ${ad.advertiseStatus ? 'bg-green-50 text-green-500 hover:bg-green-500 hover:text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-400 hover:text-white'}`} title={ad.advertiseStatus ? "Tắt" : "Bật"}>
                                                                {ad.advertiseStatus ? <Eye size={14}/> : <EyeOff size={14}/>}
                                                            </button>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>

                                        {/* Pagination Main Slider */}
                                        {totalMainSliderPages > 1 && (
                                            <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-3 border-t border-gray-100 gap-3">
                                                <span className="text-xs text-gray-500 font-medium">Hiển thị <strong>{indexOfFirstMainSlider + 1} - {Math.min(indexOfLastMainSlider, mainSliders.length)}</strong> / <strong>{mainSliders.length}</strong></span>
                                                <div className="flex flex-wrap items-center justify-center gap-3">
                                                    <div className="flex gap-1">
                                                        <button onClick={() => setMainSliderPage(Math.max(1, mainSliderPage - 1))} disabled={mainSliderPage === 1} className="p-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"><ChevronLeft size={16} /></button>
                                                        {[...Array(totalMainSliderPages)].map((_, index) => (
                                                            <button key={index} onClick={() => setMainSliderPage(index + 1)} className={`w-7 h-7 rounded text-xs font-bold transition-colors border ${mainSliderPage === index + 1 ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{index + 1}</button>
                                                        ))}
                                                        <button onClick={() => setMainSliderPage(Math.min(totalMainSliderPages, mainSliderPage + 1))} disabled={mainSliderPage === totalMainSliderPages} className="p-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"><ChevronRight size={16} /></button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* ================= TAB: SUB BANNER ================= */}
                        {activeAdTab === 'SUB_BANNER' && (
                            <div className="animate-fadeIn">
                                <div className="flex justify-end mb-3">
                                    <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                        <GripVertical size={10}/> Kéo thả hoặc nhập số để sắp xếp
                                    </span>
                                </div>

                                {subBanners.length === 0 ? (
                                    <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-sm text-gray-400">
                                        Chưa có Banner Phụ nào
                                    </div>
                                ) : (
                                    <>
                                        <ul className="flex flex-col gap-2">
                                            {currentSubBanners.map((ad, idx) => {
                                                const globalIndex = indexOfFirstSubBanner + idx;
                                                return (
                                                    <li
                                                        key={ad.advertiseId} draggable
                                                        onDragStart={(e) => handleBannerDragStart(e, ad)}
                                                        onDragEnd={handleBannerDragEnd}
                                                        onDragOver={(e) => { e.preventDefault(); setDragOverBannerId(ad.advertiseId); }}
                                                        onDragLeave={() => setDragOverBannerId(null)}
                                                        onDrop={(e) => handleBannerDrop(e, ad, 'SUB_BANNER')}
                                                        className={`flex items-center gap-3 p-2 border rounded-xl transition-all cursor-grab active:cursor-grabbing
                                                            ${draggedBanner?.advertiseId === ad.advertiseId ? 'opacity-40 bg-green-50 border-green-500' 
                                                            : dragOverBannerId === ad.advertiseId ? 'border-green-500 bg-green-50 scale-[1.01]'
                                                            : 'bg-white border-gray-200 hover:border-green-400 shadow-sm'}`}
                                                    >
                                                        <GripVertical size={16} className="text-gray-300 shrink-0 select-none" />
                                                        <input
                                                            type="number" min="1" max={subBanners.length} defaultValue={globalIndex + 1}
                                                            key={`order-sub-${ad.advertiseId}-${globalIndex}`}
                                                            onBlur={(e) => handleManualReorderBanner(ad, e.target.value, 'SUB_BANNER')}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            onClick={(e) => e.stopPropagation()} onDragStart={(e) => e.stopPropagation()}
                                                            className="w-12 h-7 text-center text-xs font-bold border border-gray-200 rounded outline-none focus:border-green-500 cursor-text shrink-0"
                                                            title={`Vị trí: ${globalIndex + 1}/${subBanners.length}`}
                                                        />
                                                        <div className="w-20 h-12 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shrink-0">
                                                            <img src={getImageUrl(ad.advertiseImageUrl)} alt={ad.advertiseTitle} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = getImageUrl(null); }} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-bold text-gray-800 truncate">{ad.advertiseTitle}</p>
                                                            <p className="text-[11px] text-green-600 truncate">{ad.advertiseUrl || 'Không có link đích'}</p>
                                                        </div>
                                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${ad.advertiseStatus ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                                            {ad.advertiseStatus ? 'ON' : 'OFF'}
                                                        </span>
                                                        <div className="flex gap-1 shrink-0">
                                                            <button onClick={() => handleEditAd(ad)} className="p-1.5 bg-blue-50 text-blue-500 rounded hover:bg-blue-500 hover:text-white transition-colors" title="Sửa"><Edit size={14}/></button>
                                                            <button onClick={() => handleDeleteAd(ad.advertiseId)} className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-500 hover:text-white transition-colors" title="Xóa"><Trash2 size={14}/></button>
                                                            <button onClick={() => handleToggleAdStatus(ad.advertiseId, ad.advertiseStatus)} className={`p-1.5 rounded transition-colors ${ad.advertiseStatus ? 'bg-green-50 text-green-500 hover:bg-green-500 hover:text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-400 hover:text-white'}`} title={ad.advertiseStatus ? "Tắt" : "Bật"}>
                                                                {ad.advertiseStatus ? <Eye size={14}/> : <EyeOff size={14}/>}
                                                            </button>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>

                                        {/* Pagination Sub Banner */}
                                        {totalSubBannerPages > 1 && (
                                            <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-3 border-t border-gray-100 gap-3">
                                                <span className="text-xs text-gray-500 font-medium">Hiển thị <strong>{indexOfFirstSubBanner + 1} - {Math.min(indexOfLastSubBanner, subBanners.length)}</strong> / <strong>{subBanners.length}</strong></span>
                                                <div className="flex flex-wrap items-center justify-center gap-3">
                                                    <div className="flex gap-1">
                                                        <button onClick={() => setSubBannerPage(Math.max(1, subBannerPage - 1))} disabled={subBannerPage === 1} className="p-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"><ChevronLeft size={16} /></button>
                                                        {[...Array(totalSubBannerPages)].map((_, index) => (
                                                            <button key={index} onClick={() => setSubBannerPage(index + 1)} className={`w-7 h-7 rounded text-xs font-bold transition-colors border ${subBannerPage === index + 1 ? 'bg-green-500 text-white border-green-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{index + 1}</button>
                                                        ))}
                                                        <button onClick={() => setSubBannerPage(Math.min(totalSubBannerPages, subBannerPage + 1))} disabled={subBannerPage === totalSubBannerPages} className="p-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"><ChevronRight size={16} /></button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {blocker.state === 'blocked' && (
                <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Rời khỏi trang?</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Bạn có thay đổi chưa được lưu. Nếu rời đi, các thay đổi này sẽ bị mất.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button 
                                onClick={() => blocker.reset()} 
                                className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-lg border border-gray-200"
                            >
                                Ở lại
                            </button>
                            <button 
                                onClick={() => {
                                    setIsAdFormDirty(false);
                                    setIsNewsCatFormDirty(false);
                                    blocker.proceed();
                                }}
                                className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg"
                            >
                                Rời đi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminContent;